import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  DOCUMENT,
  inject
} from '@angular/core';
import { Observable, Subject, delay, filter, of, tap } from 'rxjs';

/**
 * Scrolls the page to and focuses the element where this directive is used on.
 * It has some optional properties to control, when and which element should be scrolled to.
 */
@Directive({
  selector: '[nowWbsucheLibScrollToAndFocus]'
})
export class ScrollToAndFocusDirective implements OnDestroy, OnInit, AfterViewInit {
  private readonly element = inject(ElementRef);
  private readonly document = inject<Document>(DOCUMENT);

  /**
   * (Optional) A boolean or observable that can trigger whether it should scroll to the element or not (default: true).
   */
  @Input() shouldScroll: boolean | Observable<any> = true;
  /**
   * (Optional) A boolean or observable that can trigger whether it should also focus the element or not (default: true).
   */
  @Input() shouldFocus: boolean | Observable<any> = true;
  /**
   * (Optional) parameter, to control which element should be focused.
   */
  @Input() elementToFocus?: HTMLElement | null;
  /**
   * (Optional) time in milliseconds before the page scrolls, after the `shouldScroll` is triggered.
   */
  @Input() debounce = 0;

  /**
   * (Optional) Event that is triggered, when the element gets visible on the viewport.
   */
  @Output() visible = new EventEmitter<HTMLElement>();

  private observer: IntersectionObserver | undefined;
  private static readonly SCROLL_OFFSET = 55;
  private readonly subject$ = new Subject<{
    entry: IntersectionObserverEntry;
    observer: IntersectionObserver;
  }>();

  ngOnInit() {
    this.createObserver();
  }

  ngAfterViewInit() {
    this.waitForCondition(this.shouldScroll)
      .pipe(delay(this.debounce))
      .subscribe(() => {
        this.scrollToElement();
      });
    this.startObservingElements();
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = undefined;
    }

    this.subject$.complete();
  }

  private isVisible(element: HTMLElement) {
    return new Promise((resolve) => {
      const observer = new this.document.defaultView!.IntersectionObserver(([entry]) => {
        resolve(entry.intersectionRatio === 1);
        observer.disconnect();
      });

      observer.observe(element);
    });
  }

  private createObserver() {
    const options = {
      rootMargin: '0px',
      threshold: 1
    };

    const isIntersecting = (entry: IntersectionObserverEntry) =>
      entry.isIntersecting || entry.intersectionRatio > 0;

    // Oberver pattern helps focusing in Firefox
    // The observer "triggers" as soon as the intersecting element
    // is in the viewport of the browser
    this.observer = new this.document.defaultView!.IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (isIntersecting(entry)) {
          this.subject$.next({ entry, observer });
        }
      });
    }, options);
  }

  private startObservingElements() {
    if (!this.observer) {
      return;
    }

    this.observer.observe(this.element.nativeElement);

    this.subject$.pipe(delay(this.debounce), filter(Boolean)).subscribe(({ entry, observer }) => {
      (async () => {
        const target = entry.target as HTMLElement;
        const isStillVisible = await this.isVisible(target);

        if (isStillVisible) {
          this.visible.emit(target);
          this.waitForCondition(this.shouldFocus).subscribe(() => {
            this.focusElement();
          });

          observer?.unobserve(target);
        }
      })();
    });
  }

  private waitForCondition(condition: boolean | Observable<null>): Observable<null> {
    if (condition === true) {
      return this.runWithFirefoxWorkaround();
    } else if (condition instanceof Observable) {
      return condition.pipe(
        tap(() => {
          return this.runWithFirefoxWorkaround();
        })
      );
    }
    return of();
  }

  private runWithFirefoxWorkaround(): Observable<null> {
    if (this.isFirefox) {
      // Firefox needs a delay before it can scroll exactly to the element.
      return of(null).pipe(delay(200));
    } else {
      return of(null);
    }
  }

  private scrollToElement(): void {
    const distanceToPageStart =
      this.document.documentElement.scrollTop +
      this.element.nativeElement.getBoundingClientRect().y -
      this.baHeaderHeight -
      ScrollToAndFocusDirective.SCROLL_OFFSET;

    this.document.defaultView!.scrollTo({ top: distanceToPageStart });
  }

  private readonly focusElement = () => {
    if (this.elementToFocus) {
      this.elementToFocus.focus();
    } else {
      this.element.nativeElement.focus();
    }
  };

  get isFirefox(): boolean {
    return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
  }

  get baHeaderHeight(): number {
    const header = this.document.getElementsByTagName('now-wbsuche-lib-header')[0];
    let height = 0;
    height = header?.getBoundingClientRect().height;

    return height;
  }
}
